import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppThunkAPIType } from "../../../app/appTypes";
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http";

export type ReleaseStatus = "DRAFT" | "PUBLISHED";
export type ReleaseType = "HF" | "RELEASE";

export interface Release {
    id: number;
    title: string;
    titleEN: string;
    content: string;
    contentEN: string;
    status: ReleaseStatus;
    releaseType: ReleaseType;
    releaseNo: number;
    releaseVersion: number;
    publishDate: Date | null;
    publisher: string | null;
    createdBy: string;
    createDate: Date;
    modifiedBy: string;
    modifyDate: Date;
}

export interface ReleaseNotificationHistory {
    id: string;
    releaseId: number;
    publishDate: Date;
    userName: string;
    language: "PL" | "EN";
}

export interface CreateReleasePayload {
    releaseType: string;
}

export interface EditReleasePayload extends CreateReleasePayload {
    id: string;
}

export interface TestReleasePayload {
    id: string;
    email: string;
    language: string;
}

export interface PublishReleasePayload {
    id: string;
    publisherName: string;
    additionalAddresses: string;
}

export interface Bug {
    id: string;
    title: string;
    description: string;
    createDate: Date;
}

export interface UsersWithReleaseNotification {
    userName: string;
    companyName: string;
    language: string;
}

export const loadReleases = createAsyncThunk<Release[], void, AppThunkAPIType>("system-releases/load", async (_, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(getWithAuth({ url: `api/sys/releases` }))
    const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
        return payload as Release[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadRelease = createAsyncThunk<Release, string, AppThunkAPIType>("system-releases/load-one", async (id, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(getWithAuth({ url: `api/sys/releases/${id}` }))
    const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
        return payload as Release
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadReleaseDetails = createAsyncThunk<Release, string, AppThunkAPIType>("system-releases/load-details", async (id, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(getWithAuth({ url: `api/sys/releases/${id}/details` }))
    const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
        return payload as Release
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadReleaseNotificationHistory = createAsyncThunk<ReleaseNotificationHistory[], number, AppThunkAPIType>("system-releases/load-notification-history", async (id, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(getWithAuth({ url: `api/sys/releases/${id}/notification-history` }))
    const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
        return payload as ReleaseNotificationHistory[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadBugs = createAsyncThunk<Bug[], void, AppThunkAPIType>("system-releases/load", async (_, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(getWithAuth({ url: `api/sys/releases/bugs` }))
    const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
        return payload as Bug[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadUsersWithReleaseNotification = createAsyncThunk<UsersWithReleaseNotification[], void, AppThunkAPIType>("system-releases/load-users", async (_, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(getWithAuth({ url: `api/sys/releases/users` }))
    const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
        return payload as UsersWithReleaseNotification[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const createRelease = createAsyncThunk<Release, CreateReleasePayload, AppThunkAPIType>("system-releases/create", async (release, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(postWithAuth({ url: `api/sys/releases`, payload: release }))
    const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
        return payload as Release
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const editRelease = createAsyncThunk<Release, EditReleasePayload, AppThunkAPIType>("system-releases/create", async (release, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(putWithAuth({ url: `api/sys/releases/${release.id}/edit`, payload: release }))
    const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
        return payload as Release
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const sendTestRelease = createAsyncThunk<Release, TestReleasePayload, AppThunkAPIType>("system-releases/test-message", async (release, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(putWithAuth({ url: `api/sys/releases/${release.id}/test-message`, payload: release }))
    const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
        return payload as Release
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const publishRelease = createAsyncThunk<Release, PublishReleasePayload, AppThunkAPIType>("system-releases/create", async (release, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(putWithAuth({ url: `api/sys/releases/${release.id}/publish`, payload: { publisherName: release.publisherName, additionalAddresses: release.additionalAddresses } }))
    const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
        return payload as Release
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const deleteRelease = createAsyncThunk<Release, string, AppThunkAPIType>("system-releases/delete", async (id, { dispatch, rejectWithValue, requestId, getState }) => {
    const result = await dispatch(deleteWithAuth({ url: `api/sys/releases/${id}` }))
    const { payload } = result
        if (deleteWithAuth.fulfilled.match(result)) {
        return payload as Release
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})